import React, { useState, useEffect } from 'react';
import { completeUserProfile, checkProfileStatus } from '../services/auth';
import DefaultAvatar from '../assets/default-avatar.png'; // You'll need to add this image

const ProfileSetup = ({ userId, onComplete }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [previewImage, setPreviewImage] = useState(DefaultAvatar);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusChecked, setStatusChecked] = useState(false);

  // Check if user has already completed profile setup
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await checkProfileStatus(userId);
        if (status.hasCompletedProfile) {
          // User has already completed profile setup
          onComplete();
        }
        setStatusChecked(true);
      } catch (error) {
        console.error('Error checking profile status:', error);
        setError('Unable to check profile status');
        setStatusChecked(true);
      }
    };

    checkStatus();
  }, [userId, onComplete]);

  // Handle file upload for profile picture
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Profile picture must be less than 5MB');
      return;
    }

    // Check file type (allow only images)
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImage(event.target.result);
      setProfilePicture(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (username.length < 3 || username.length > 20) {
      setError('Username must be between 3 and 20 characters');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await completeUserProfile(userId, {
        username,
        password,
        profilePicture
      });
      
      // Call the onComplete callback
      onComplete();
    } catch (error) {
      setError(error.message || 'Failed to complete profile setup');
    } finally {
      setIsLoading(false);
    }
  };

  if (!statusChecked) {
    return (
      <div className="profile-setup-loading">
        <div className="spinner"></div>
        <p>Checking profile status...</p>
      </div>
    );
  }

  return (
    <div className="profile-setup-container">
      <h2>Complete Your Profile</h2>
      <p className="setup-info">
        Set up your username and password to complete your registration.
      </p>
      
      {error && (
        <div className="profile-setup-error">
          <p>{error}</p>
          <button 
            onClick={() => setError('')}
            className="dismiss-error"
          >
            Dismiss
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="profile-setup-form">
        <div className="profile-picture-section">
          <div className="profile-image-container">
            <img 
              src={previewImage} 
              alt="Profile" 
              className="profile-image-preview" 
            />
            <label htmlFor="profile-picture" className="change-picture-label">
              Change Picture
            </label>
          </div>
          <input
            type="file"
            id="profile-picture"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            onChange={handleFileChange}
            className="file-input"
          />
          <p className="picture-help">Upload a profile picture (optional)</p>
        </div>
        
        <div className="form-group">
          <label htmlFor="username">Username*</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Choose a unique username"
            disabled={isLoading}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="password">Password*</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a secure password"
            disabled={isLoading}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password*</label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            disabled={isLoading}
            required
          />
        </div>
        
        <button
          type="submit"
          className="submit-profile-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Setting Up...' : 'Complete Setup'}
        </button>
      </form>
    </div>
  );
};

export default ProfileSetup;